import { z } from "zod";
import { assessClaim, ClaimTypeSchema, InferenceModeSchema, StanceSchema, VerificationSchema, } from "./epistemic.js";
/**
 * Deterministic impact (cascade v1).
 *
 * Answers "what explicitly depends on this claim?" by inverting recorded
 * `derived_from` claim references. It does not search, suggest, or write.
 * Evidence and fact references are not inverted. Callers must pass only
 * claims the viewer may see; a hidden claim is omitted, and the walk does
 * not bridge through it.
 */
export const IMPACT_HEADLINE = "Underlying knowledge changed";
export const IMPACT_CONCLUSION = "This research conclusion depends on a claim that has since been superseded.";
export const IMPACT_POSITION = "This recorded position may need review because one of its explicit inputs changed.";
export const IMPACT_POSITION_UNCHANGED = "The position has not changed.";
export const IMPACT_SCENARIO = "Under this scenario";
export const IMPACT_HISTORICAL = "Historical / set aside";
const ImpactStepSchema = z.object({
    id: z.string().uuid(),
    proposition: z.string(),
});
export const ClaimImpactNodeSchema = z.object({
    id: z.string().uuid(),
    proposition: z.string(),
    subjectId: z.string().uuid(),
    subjectLabel: z.string(),
    claimType: ClaimTypeSchema,
    inferenceMode: InferenceModeSchema.nullable(),
    condition: z.string().nullable(),
    verification: VerificationSchema,
    organisationStance: StanceSchema.nullable(),
    superseded: z.boolean(),
    setAside: z.boolean(),
    conditional: z.boolean(),
    historical: z.boolean(),
    depth: z.number().int().positive(),
    direct: z.boolean(),
    path: z.array(ImpactStepSchema),
    inquiryId: z.string().uuid().nullable(),
    inquiryQuestion: z.string().nullable(),
});
export const ClaimImpactSchema = z.object({
    changedClaimId: z.string().uuid(),
    changedProposition: z.string(),
    changedSubjectId: z.string().uuid(),
    changedSubjectLabel: z.string(),
    changedSuperseded: z.boolean(),
    direct: z.array(ClaimImpactNodeSchema),
    downstream: z.array(ClaimImpactNodeSchema),
});
function claimRefs(claim) {
    return claim.derivedFrom.filter((ref) => ref.kind === "claim").map((ref) => ref.id);
}
function compareId(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
/** Claims whose Subject the viewer may see. Hidden claims are dropped entirely. */
export function claimsVisibleForImpact(claims, viewableSubjectIds) {
    return claims.filter((row) => viewableSubjectIds.has(row.subjectId));
}
function childrenBySource(claims) {
    const children = new Map();
    for (const claim of claims) {
        for (const sourceId of claimRefs(claim)) {
            const list = children.get(sourceId) ?? [];
            list.push(claim.id);
            children.set(sourceId, list);
        }
    }
    for (const list of children.values())
        list.sort(compareId);
    return children;
}
function reachesQuestion(claim, questionId, byId, seen) {
    if (claim.id === questionId)
        return true;
    if (seen.has(claim.id))
        return false;
    seen.add(claim.id);
    for (const id of claimRefs(claim)) {
        if (id === questionId)
            return true;
        const next = byId.get(id);
        if (next && reachesQuestion(next, questionId, byId, seen))
            return true;
    }
    return false;
}
function inquiryFor(claim, byId) {
    const questions = [...byId.values()]
        .filter((row) => row.claimType === "question" && row.supersededAt === null)
        .sort((a, b) => compareId(a.id, b.id));
    const cited = new Set(claimRefs(claim));
    const direct = questions.find((row) => cited.has(row.id));
    if (direct)
        return direct;
    return questions.find((row) => reachesQuestion(claim, row.id, byId, new Set())) ?? null;
}
function toNode(claim, depth, pathIds, byId, labels) {
    const assessment = assessClaim(claim, (id) => byId.get(id) ?? null);
    const stance = assessment.selfStance?.stance ?? null;
    const superseded = claim.supersededAt !== null;
    const setAside = superseded || stance === "rejected";
    const inquiry = inquiryFor(claim, byId);
    return {
        id: claim.id,
        proposition: claim.proposition,
        subjectId: claim.subjectId,
        subjectLabel: labels.get(claim.subjectId) ?? "",
        claimType: claim.claimType,
        inferenceMode: claim.inferenceMode,
        condition: claim.condition,
        verification: assessment.verification,
        organisationStance: stance,
        superseded,
        setAside,
        conditional: assessment.conditional || Boolean(claim.condition?.trim()),
        historical: setAside,
        depth,
        direct: depth === 1,
        path: pathIds.map((id) => {
            const row = byId.get(id);
            return { id, proposition: row?.proposition ?? "" };
        }),
        inquiryId: inquiry?.id ?? null,
        inquiryQuestion: inquiry?.proposition ?? null,
    };
}
/**
 * Forward walk from one claim through explicit claim references.
 * The changed claim itself is not a dependant. Cycles stop. Each claim
 * appears once, on the first deterministic path (breadth, then id order).
 * The walk continues until the queue is empty. Depth is metadata. The
 * authorised claim set passed in is the bound; a depth cutoff would omit
 * a real dependency and present that omission as completeness.
 */
export function whatDependsOn(claimId, claims, labels = new Map(), changed) {
    const byId = new Map(claims.map((row) => [row.id, row]));
    const root = changed ?? byId.get(claimId);
    const children = childrenBySource(claims);
    const seen = new Set([claimId]);
    const queue = [];
    const enqueue = (id, path, depth) => {
        if (seen.has(id))
            return;
        if (path.includes(id))
            return;
        seen.add(id);
        queue.push({ id, path: [...path, id], depth });
    };
    for (const id of children.get(claimId) ?? [])
        enqueue(id, [claimId], 1);
    const downstream = [];
    while (queue.length > 0) {
        const frame = queue.shift();
        if (!frame)
            break;
        const row = byId.get(frame.id);
        if (!row)
            continue;
        downstream.push(toNode(row, frame.depth, frame.path, byId, labels));
        for (const next of children.get(frame.id) ?? []) {
            enqueue(next, frame.path, frame.depth + 1);
        }
    }
    downstream.sort((a, b) => a.depth - b.depth || compareId(a.id, b.id));
    return {
        changedClaimId: claimId,
        changedProposition: root?.proposition ?? "",
        changedSubjectId: root?.subjectId ?? claimId,
        changedSubjectLabel: root ? (labels.get(root.subjectId) ?? "") : "",
        changedSuperseded: root?.supersededAt != null,
        direct: downstream.filter((row) => row.direct),
        downstream,
    };
}
export function impactFromVisibleClaims(claimId, claims, viewableSubjectIds, labels = new Map()) {
    const visible = claimsVisibleForImpact(claims, viewableSubjectIds);
    const root = visible.find((row) => row.id === claimId) ?? claims.find((row) => row.id === claimId);
    return whatDependsOn(claimId, visible, labels, root && viewableSubjectIds.has(root.subjectId) ? root : undefined);
}
export function recordedPositionStance(stance) {
    return stance === "adopted" || stance === "agreed";
}
//# sourceMappingURL=claim-impact.js.map